export interface FeatureItem {
  id: number;
  tag: string;
  headline: string;
  body: string;
  icon: string;
}

export interface FeatureSection {
  id: string;
  heading: string;
  subheading: string;
  features: FeatureItem[];
}

export const featureSections: FeatureSection[] = [
  {
    id: 'search',
    heading: 'Search & Coverage',
    subheading:
      'One query reaches every broker. Filter what you need, see what you get.',
    features: [
      {
        id: 1,
        tag: 'Multi-Broker Query',
        headline: 'Four-broker search in one query',
        body: 'Echo, CH Robinson, Uber Freight, and RXO all feed into a single unified list. No more flipping between portals or running the same search four times.',
        icon: 'ri-search-2-line',
      },
      {
        id: 2,
        tag: 'Unified Format',
        headline: 'Unified load format',
        body: 'Rate, distance, weight, equipment, and stops are normalized across every broker. Compare apples to apples, not whatever each portal decides to show.',
        icon: 'ri-stack-line',
      },
      {
        id: 3,
        tag: 'Radius Search',
        headline: 'Origin + destination with radius',
        body: 'City and state autocomplete covers thousands of US cities. Set adjustable pickup and drop-off radius so you catch loads near your preferred lanes.',
        icon: 'ri-map-pin-2-line',
      },
      {
        id: 4,
        tag: 'Date Filtering',
        headline: 'Pickup and delivery date filters',
        body: 'Search by single date, date range, or multi-day pickup window. Delivery-date filtering is applied wherever the broker supports it.',
        icon: 'ri-calendar-check-line',
      },
      {
        id: 5,
        tag: 'Equipment Filter',
        headline: 'Equipment filtering',
        body: 'Filter for van, reefer, and flatbed — including 48\' and 53\' variants — across every broker in one click. No manual filtering per portal.',
        icon: 'ri-truck-line',
      },
      {
        id: 6,
        tag: 'Weight Ceiling',
        headline: 'Max weight filter',
        body: 'Set one weight ceiling and it is enforced across all results, even when a broker does not support weight filtering on its own portal.',
        icon: 'ri-scales-3-line',
      },
      {
        id: 7,
        tag: 'Live Counts',
        headline: 'Live per-broker counts',
        body: 'Watch each broker\'s contribution update as you adjust filters. Toggle any broker in or out to see exactly who is carrying the loads you want.',
        icon: 'ri-bar-chart-box-line',
      },
    ],
  },
  {
    id: 'booking',
    heading: 'Book & Run',
    subheading: 'Get to the load fast. Stay running even when something breaks.',
    features: [
      {
        id: 8,
        tag: 'Deep-Link Booking',
        headline: 'One-click deep-link to book',
        body: 'Every load opens directly in the originating broker\'s portal. Booking happens on your existing account, with your existing rates and terms.',
        icon: 'ri-external-link-line',
      },
      {
        id: 9,
        tag: 'Resilient Search',
        headline: 'Resilient by design',
        body: 'One slow or failing broker does not break the whole search. Errors are surfaced per broker so you know what is working and what is not.',
        icon: 'ri-shield-check-line',
      },
    ],
  },
  {
    id: 'fleets',
    heading: 'Built for Fleets',
    subheading: 'Separate companies, separate data, one platform.',
    features: [
      {
        id: 10,
        tag: 'Multi-Tenant',
        headline: 'Multi-tenant for fleets',
        body: 'Per-company accounts with user roster and isolated broker credentials. Each driver or dispatcher has their own login and sees only their company\'s data.',
        icon: 'ri-building-3-line',
      },
      {
        id: 11,
        tag: 'Own Credentials',
        headline: 'Bring your own broker credentials',
        body: 'Connect your existing Echo, CH Robinson, Uber, and RXO accounts. LoadAPI is not a reseller — you keep your direct broker relationships and your rates.',
        icon: 'ri-key-2-line',
      },
      {
        id: 13,
        tag: 'Audit Log',
        headline: 'Search audit log',
        body: 'Every search is logged so admins can troubleshoot issues and review team activity without guessing who ran what and when.',
        icon: 'ri-history-line',
      },
      {
        id: 14,
        tag: 'Admin Panel',
        headline: 'Admin panel',
        body: 'Manage users, companies, and broker credentials from one place. Add a dispatcher, rotate a broker key, or audit a search in seconds.',
        icon: 'ri-dashboard-3-line',
      },
    ],
  },
  {
    id: 'performance',
    heading: 'Performance',
    subheading: 'Fast results that respect broker rate limits.',
    features: [
      {
        id: 12,
        tag: 'Smart Caching',
        headline: 'Built-in caching',
        body: 'Repeat searches reuse cached results to respect broker quotas and cut wait time. Fresh data when it matters, speed when it does not.',
        icon: 'ri-flashlight-line',
      },
    ],
  },
];